import React, {useRef, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import {createPortal} from 'react-dom'

import keyCodes from 'js/shared/keyCodes';
import SelectDropdown from './components/SelectDropdown';

import styles from './CustomSelect.module.scss'

const cx = classnames.bind(styles)

const CustomSelect = (props) => {
  const {
    className,
    dropdownClassName,
    valueClassName,
    id,
    withValidateIcons = true,
    options,
    placeholder = 'Select',
    noResultsText = 'No results ...',
    dropdownPosition = 'bottom',
    dropdownHeight,
    onClose,
    onOpen,
    disabled,
    optionRender = (el) => el.label,
    valueRender = (el) => (<span className={cx('option-content')}>{el.label}</span>),
    searchable = true,
    dropdownParent,
    errorClassname = 'error',
    validClassname = 'valid',
    input: {
      name,
      value,
      onFocus,
      onBlur,
      onChange,
    },
    meta = {}
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  const selectRef = useRef(null)
  const isFailed = useMemo(() => {
    return meta.invalid && (meta?.touched || meta?.submitFailed)
  }, [meta?.submitFailed, meta?.invalid, meta?.touched])

  const isSuccess = useMemo(() => {
    return meta.touched && !meta.invalid && !!value
  }, [meta?.invalid, meta?.touched])

  const selectOpen = function () {
    setIsOpen(true)
    onFocus && onFocus()
    onOpen && onOpen()
  }

  const selectClose = function (focus) {
    setIsOpen(false)
    onClose && onClose()
    onBlur && onBlur()

    if (focus) {
      selectRef?.current?.focus();
    }
  }

  const onSelectElement = function (el) {
    onChange && onChange(el.value)
    selectClose(true)
  }

  const selectNearbyElement = function (isPrev = false) {
    const currentActive = options.findIndex(el => el.value === value);
    let nextItemIndex;
    if (currentActive >= 0) {
      if (isPrev) {
        nextItemIndex = currentActive === 0 ? 0 : currentActive - 1
      } else {
        nextItemIndex = (currentActive + 1 ===  options.length) ? options.length - 1 : currentActive + 1
      }
    } else {
      if (isPrev) {
        nextItemIndex = options.length - 1
      } else {
        nextItemIndex = 0
      }
    }
    onSelectElement(options[nextItemIndex])
  }

  const onClickSelect = function (e) {
    e.preventDefault();
    e.stopPropagation();

    selectOpen();
  }
  const onFocusSelect = function (e) {
    setIsFocus(true)
  }

  const onBlurSelect = function (e) {
    setIsFocus(false)
  }

  const onKeyDown = function (e) {
    if (isFocus) {
      if (e.keyCode === keyCodes.enter || e.keyCode === keyCodes.space) {
        e.preventDefault()
        e.stopPropagation()

        isOpen ? selectClose() : selectOpen()
      }

      if (e.keyCode === keyCodes.arrowUp || e.keyCode === keyCodes.arrowDown) {
        e.preventDefault();
        e.stopPropagation();

        selectNearbyElement(e.keyCode === keyCodes.arrowUp)
      }
    }
  }

  const placeholderData = useMemo(function () {
    return {
      value: '',
      label: placeholder
    }
  }, [placeholder])

  const noResultsData = useMemo(function () {
    const data = {
      disabled: true,
      label: noResultsText,
      id: 'no-results'
    }
    return {
      ...data,
      template: optionRender(data)
    }
  }, [noResultsText, optionRender])

  const preparedOptions = useMemo(function () {
    return options.map(el => ({
      ...el,
      template: optionRender(el)
    }))
  }, [options, optionRender])

  const valueData = useMemo(function () {
    const selectValue = value ? value + '' : value
    return value ? options.find(el => (el.value + '') === selectValue) : undefined
  }, [value, options])

  const valueTemplate = useMemo(function () {
    return valueRender(valueData || placeholderData)
  }, [valueData, placeholderData, valueRender])

  const renderDropdown = useMemo(function () {
    const dropdown = (
      <SelectDropdown
        options={preparedOptions}
        value={value}
        position={dropdownPosition}
        height={dropdownHeight}
        searchable={searchable}
        className={dropdownClassName}
        noResultsData={noResultsData}
        onClose={selectClose}
        onSelect={onSelectElement}
      />
    )

    if (dropdownParent) {
      const dropdownParentNode = selectRef?.current?.closest(dropdownParent)
      return dropdownParentNode ? createPortal(dropdown, dropdownParentNode) : dropdown
    }
    return dropdown
  }, [
    noResultsData,
    dropdownParent,
    preparedOptions,
    value,
    searchable,
    dropdownClassName,
    onSelectElement
  ])

  return (
    <div
      className={cx('select', className, {
        [errorClassname]: isFailed,
        [validClassname]: isSuccess,
        opened: isOpen,
        'with-icons': withValidateIcons
      })}
      data-selector="select"
    >
      <select
        name={name}
        className={cx('input')}
        onFocus={onFocusSelect}
        onClick={onClickSelect}
        onBlur={onBlurSelect}
        tabIndex="-1"
        id={id}
        disabled={disabled}
        defaultValue={value}
        aria-hidden="true"
      >
        {placeholder !== undefined && (
          <option value="">{placeholder}</option>
        )}
        {options.map(({value, label}, i) => (
          <option value={value} key={value + i}>{label}</option>
        ))}
      </select>
      <div
        role="combobox"
        aria-haspopup="true"
        aria-expanded="false"
        tabIndex="0"
        ref={selectRef}
        onFocus={onFocusSelect}
        onClick={onClickSelect}
        onBlur={onBlurSelect}
        onKeyDown={onKeyDown}
        data-selector="select-field"
        className={cx('select-field', valueClassName)}
      >
        {valueTemplate}
        <span className={cx('status-icon')} role="presentation" />
      </div>
      {isOpen && (renderDropdown)}
    </div>
  )
}

CustomSelect.propTypes = {
  className: PropTypes.string,
  valueRender: PropTypes.func,
  withValidateIcons: PropTypes.bool,
  dropdownPosition: PropTypes.oneOf(['bottom', 'top']),
  dropdownHeight: PropTypes.number,
  meta: PropTypes.shape({
    invalid: PropTypes.bool,
    touched: PropTypes.bool
  }),
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }).isRequired,
  validClassname: PropTypes.string,
  errorClassname: PropTypes.string,
}

export default CustomSelect;
